/**
 * `ReliefFields` turned into GPU resources: two textures, a mip level, and the
 * mesh that matches it.
 *
 * The old upload was one RGBA32F image — sixteen bytes a texel, 164 MB for a
 * large country, and no mip chain at all, which is why the vertex shader had to
 * take nine taps by hand to keep a spire alive. This splits by precision instead:
 * height needs real floats and a max-preserving chain, everything else is a
 * bounded quantity that eight bits carries and the GPU can average down. The
 * result is both smaller and far better informed.
 */

import * as THREE from "three";

import type { ReliefFields } from "../../shared/types.ts";

/**
 * Range the coast channel encodes, in cells. Three cells at eight bits is a
 * resolution of a fortieth of a cell, well past what the edge coverage needs, and
 * nothing further out can influence a pixel.
 */
const COAST_PACK_CELLS = 3;

/** Fewest vertices worth building; below this the LOD saves nothing that matters. */
const MIN_LEVEL_VERTICES = 4096;

export interface ReliefUpload {
  /** R32F with a max-preserving manual mip chain. */
  height: THREE.DataTexture;
  /** RGBA8: population, terrain, coast distance, cosine of slope. */
  shade: THREE.DataTexture;
  /** Encoded range of the coast channel, in normalised projection units. */
  coastRange: number;
  dispose(): void;
}

export function packRelief(fields: ReliefFields, anisotropy: number): ReliefUpload {
  const cells = fields.width * fields.height;
  const coastRange = COAST_PACK_CELLS * Math.max(fields.cellX, fields.cellY);

  const height = new THREE.DataTexture(
    fields.heightMips[0].data,
    fields.width,
    fields.height,
    THREE.RedFormat,
    THREE.FloatType,
  );
  height.mipmaps = fields.heightMips.map((level) => ({
    data: level.data,
    width: level.width,
    height: level.height,
  }));
  height.generateMipmaps = false;
  // Linear inside a level and across levels: the shader asks for whole levels, so
  // what this really buys is a defined filter for the fragment's neighbour taps.
  height.minFilter = THREE.LinearMipmapLinearFilter;
  height.magFilter = THREE.LinearFilter;
  height.wrapS = THREE.ClampToEdgeWrapping;
  height.wrapT = THREE.ClampToEdgeWrapping;
  height.needsUpdate = true;

  const packed = new Uint8Array(cells * 4);
  const coastScale = 127.5 / coastRange;
  for (let index = 0; index < cells; index += 1) {
    const target = index * 4;
    packed[target] = quantise(fields.population[index]);
    packed[target + 1] = quantise(fields.terrain[index]);
    const distance = fields.coast[index] * coastScale;
    packed[target + 2] = 127.5 + (distance < -127.5 ? -127.5 : distance > 127.5 ? 127.5 : distance);
    packed[target + 3] = quantise(fields.cosSlope[index]);
  }
  const shade = new THREE.DataTexture(
    packed,
    fields.width,
    fields.height,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  );
  shade.generateMipmaps = true;
  shade.minFilter = THREE.LinearMipmapLinearFilter;
  shade.magFilter = THREE.LinearFilter;
  shade.wrapS = THREE.ClampToEdgeWrapping;
  shade.wrapT = THREE.ClampToEdgeWrapping;
  // Data, not colour: any transfer-function decode here would be nonsense.
  shade.colorSpace = THREE.NoColorSpace;
  shade.anisotropy = anisotropy;
  shade.needsUpdate = true;

  return {
    height,
    shade,
    coastRange,
    dispose(): void {
      height.dispose();
      shade.dispose();
    },
  };
}

function quantise(value: number): number {
  const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
  return (clamped * 255 + 0.5) | 0;
}

/**
 * Coarsest mip level whose texel count fits the vertex budget.
 *
 * The LOD is a whole mip level rather than an arbitrary segment count, which is
 * what lets the mesh sit on texel centres — and that alignment is the difference
 * between shading that describes the geometry and shading that merely resembles
 * it. It also means small zoom changes never rebuild anything.
 */
export function reliefLevel(fields: ReliefFields, budget: number): number {
  const levels = fields.heightMips.length;
  for (let level = 0; level < levels; level += 1) {
    const mip = fields.heightMips[level];
    const vertices = mip.width * mip.height;
    if (vertices <= budget) return level;
    if (vertices <= MIN_LEVEL_VERTICES) return level;
  }
  return levels - 1;
}

/**
 * The displaced plane for a level, with one vertex per texel placed on that
 * texel's centre.
 *
 * Spanning the whole extent instead would put vertices on texel *edges*, where a
 * linear fetch returns the mean of two neighbours — halving every spire the
 * max-preserving chain just went to the trouble of keeping.
 */
export function reliefGeometry(fields: ReliefFields, level: number): THREE.PlaneGeometry {
  const mip = fields.heightMips[level];
  const columns = Math.max(mip.width, 2);
  const rows = Math.max(mip.height, 2);
  const spanX = fields.extent[1] - fields.extent[0];
  const spanY = fields.extent[3] - fields.extent[2];

  const geometry = new THREE.PlaneGeometry(
    (spanX * (columns - 1)) / columns,
    (spanY * (rows - 1)) / rows,
    columns - 1,
    rows - 1,
  );
  // The fragment derives the normal from the height field and there are no maps,
  // so neither attribute is ever read.
  geometry.deleteAttribute("normal");
  geometry.deleteAttribute("uv");
  geometry.translate(
    (fields.extent[0] + fields.extent[1]) * 0.5,
    (fields.extent[2] + fields.extent[3]) * 0.5,
    0,
  );
  return geometry;
}
